<script setup lang="ts">
import type { PreviewLocale, PreviewTheme } from '@open-file-viewer/core'
import { useFileViewer } from '@/utils/composables/useFileViewer'
import { OpenFileViewer } from '@open-file-viewer/vue'
import { useMediaQuery, useScrollLock } from '@vueuse/core'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import {
  createFileViewerToolbar,
  fileViewerPlugins,
  spreadsheetFileExtensions,
  spreadsheetMimeTypes,
} from './config'
import '@open-file-viewer/core/style.css'

defineOptions({
  name: 'FileViewerHost',
})

const { current, closeFileViewer } = useFileViewer()
const settingsStore = useSettingsStore()
const { locale, t } = useI18n()
const route = useRoute()
const maskRef = ref<HTMLElement | null>(null)
const isMobile = useMediaQuery('(max-width: 767px)')
const bodyScrollLocked = useScrollLock(document.body)

const theme = computed<PreviewTheme>(() => settingsStore.currentColorScheme === 'dark' ? 'dark' : 'light')
const viewerLocale = computed<PreviewLocale>(() => locale.value === 'en-us' ? 'en-US' : 'zh-CN')
const fileViewerToolbar = computed(() => createFileViewerToolbar({
  zoomOut: t('toolbar.zoomOut'),
  zoomIn: t('toolbar.zoomIn'),
  resetZoom: t('toolbar.resetZoom'),
  fullscreen: t('toolbar.fullscreen'),
}))
const viewerHeight = computed(() => isMobile.value
  ? 'calc(100vh - var(--van-nav-bar-height, 46px) - env(safe-area-inset-top))'
  : 'calc(100vh - 64px)')
const isSpreadsheet = computed(() => {
  const fileName = current.value?.fileName.toLowerCase() || ''
  const extensionIndex = fileName.lastIndexOf('.')
  const extension = extensionIndex >= 0 ? fileName.slice(extensionIndex + 1) : ''
  const mimeType = current.value?.mimeType.toLowerCase() || ''

  return spreadsheetFileExtensions.has(extension) || spreadsheetMimeTypes.has(mimeType)
})

watch(current, (request) => {
  bodyScrollLocked.value = Boolean(request)
  if (request) {
    void nextTick(() => maskRef.value?.focus())
  }
}, { immediate: true })

watch(() => route.fullPath, () => {
  close()
})

onBeforeUnmount(() => {
  bodyScrollLocked.value = false
  close()
})

function close() {
  const fullscreenElement = document.fullscreenElement
  if (fullscreenElement && maskRef.value?.contains(fullscreenElement)) {
    void document.exitFullscreen().catch(() => {})
  }
  closeFileViewer()
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="current"
      ref="maskRef"
      class="global-file-viewer-mask"
      :class="{ 'is-spreadsheet': isSpreadsheet }"
      role="dialog"
      aria-modal="true"
      :aria-label="current.title || current.fileName"
      tabindex="-1"
      @click.self="close"
      @keydown.esc.stop="close"
    >
      <van-nav-bar
        v-if="isMobile"
        class="global-file-viewer-navbar"
        :title="current.title || current.fileName"
        safe-area-inset-top
        left-arrow
        @click-left="close"
      />
      <OpenFileViewer
        :key="current.requestId"
        :file="current.file"
        :file-name="current.fileName"
        :mime-type="current.mimeType"
        class-name="global-open-file-viewer"
        width="100%"
        :height="viewerHeight"
        fit="contain"
        :plugins="fileViewerPlugins"
        :toolbar="fileViewerToolbar"
        :theme="theme"
        :locale="viewerLocale"
      />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.global-file-viewer-mask {
  position: fixed;
  inset: 0;
  z-index: 4100;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 100px;
  overflow: hidden;
  overscroll-behavior: contain;
  outline: none;
  background: rgb(0 0 0 / 72%);
  backdrop-filter: blur(2px);
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-viewport) {
  overflow: hidden;
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-office) {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-tabs) {
  flex: 0 0 auto;
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-sheet) {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-sheet > h3),
.global-file-viewer-mask.is-spreadsheet :deep(.ofv-sheet > .ofv-sheet-summary),
.global-file-viewer-mask.is-spreadsheet :deep(.ofv-sheet > .ofv-sheet-window) {
  flex: 0 0 auto;
}

.global-file-viewer-mask.is-spreadsheet :deep(.ofv-sheet > .ofv-table-scroll) {
  flex: 1 1 0;
  min-height: 0;
  max-height: none;
}

.global-file-viewer-mask :deep(.ofv-code-actions) {
  display: none;
}

.global-file-viewer-navbar {
  flex: 0 0 auto;
  width: 100%;
}

@media (width < 768px) {
  .global-file-viewer-mask {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    padding: 0;
  }
}
</style>
